import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TVCard from '@/components/base/TVCard';
import { tvSeriesList, categories } from '@/mocks/tv-series';
import type { TVSeries } from '@/mocks/types';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [filteredSeries, setFilteredSeries] = useState<TVSeries[]>([]);
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    let result = activeCategory === 'all'
      ? [...tvSeriesList]
      : tvSeriesList.filter((s) => s.category === activeCategory);

    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'newest') {
      result.sort((a, b) => b.year - a.year);
    } else {
      result.sort((a, b) => b.rating - a.rating);
    }

    setFilteredSeries(result);
  }, [activeCategory, sortBy]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory?.name || '全部';

  return (
    <>
      <SEOHead
        title={`${categoryName} - 小草影院电视剧分类浏览`}
        description={`小草影院${categoryName}频道，提供最新${categoryName}高清全集在线观看。涵盖热门${categoryName}，每日更新，高清画质即点即看。`}
        keywords={`小草影院,${categoryName},${categoryName}在线观看,${categoryName}高清全集`}
        canonicalUrl={`https://www.xiaocaoyingyuan.com/category/${activeCategory}`}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '小草影院首页', item: 'https://www.xiaocaoyingyuan.com/' },
            { '@type': 'ListItem', position: 2, name: `${categoryName}分类`, item: `https://www.xiaocaoyingyuan.com/category/${activeCategory}` },
          ],
        }}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-4">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: `${categoryName}` },
          ]}
        />

        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-2 mb-6">
          {categoryName}
        </h1>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">排序：</span>
            {[
              { key: 'popular' as const, label: '最热' },
              { key: 'newest' as const, label: '最新' },
              { key: 'rating' as const, label: '评分' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === opt.key
                    ? 'bg-accent-100 text-accent-700 font-medium'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {filteredSeries.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
            {filteredSeries.map((series) => (
              <TVCard key={series.id} series={series} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">暂无相关内容，请尝试其他分类</p>
          </div>
        )}

        <div className="py-10 text-center">
          <p className="text-sm text-foreground-400">
            共 {filteredSeries.length} 部{categoryName}
          </p>
        </div>
      </div>
    </>
  );
}